import type * as React from "react";

import { useDensity } from "@/hooks/use-density";
import { cn } from "@/lib/utils";

// Standard shadcn/ui Table primitive. Provides consistent typography, header
// weight, row dividers, and hover treatment across every data table in the
// app. Callers compose <Table> → <TableHeader> / <TableBody> / <TableFooter>
// → <TableRow> → <TableHead> / <TableCell>.
//
// Row density (comfortable / compact) is read from the global Density
// context — TableHead and TableCell adapt their padding/height so callers
// never have to thread the value through.

// The default wrapper has `overflow-x-auto` so narrow tables can scroll
// sideways on small viewports. That wrapper implicitly creates a vertical
// scroll container too, which steals `position: sticky` from the page's
// `<main>` scroller — so sticky <thead>s pin to the wrapper instead of
// the page. Pages that need a sticky thead can pass `horizontalScroll={false}`
// to render the table without the overflow wrapper.
function Table({
  className,
  horizontalScroll = true,
  ...props
}: React.ComponentProps<"table"> & { horizontalScroll?: boolean }) {
  return (
    <div className={cn("relative w-full", horizontalScroll && "overflow-x-auto")}>
      <table className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return <thead className={cn("[&_tr]:border-b", className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      className={cn("border-t bg-muted/30 font-medium [&>tr]:last:border-b-0", className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      className={cn(
        "border-b transition-colors hover:bg-muted/30 data-[state=selected]:bg-muted",
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  const { density } = useDensity();
  return (
    <th
      className={cn(
        "px-2 text-left align-middle font-medium text-muted-foreground text-xs [&:has([role=checkbox])]:pr-0",
        density === "compact" ? "h-7" : "h-9",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  const { density } = useDensity();
  return (
    <td
      className={cn(
        "px-2 align-middle [&:has([role=checkbox])]:pr-0",
        density === "compact" ? "py-1.5" : "py-2",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<"caption">) {
  return <caption className={cn("mt-4 text-muted-foreground text-sm", className)} {...props} />;
}

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
